import { useTransformStringOnOffset } from '@/utils/hooks/format/useTransformString';

import { classNames } from '@/utils';

import type { XMediaPartialType, XTagType } from '@/components/pages/content/types';

import { MEDIA_TYPES } from '@/lib/medias/mediaTypes';
import { POST, TW_POST_TYPES } from '@/lib/posts/postTypes';
import { X } from '@/lib/socialProfile';

import Carousel from '@/components/base/Carousel';
import Icon from '@/components/base/Icon';
import { Item } from '@/components/composite/MediaViewer/PostPreview/Item';
import { InitialTweetAuthor } from '@/components/composite/MediaViewer/PostPreview/PreviewTwitter/InitialTweetAuthor';
import { QuotedTweetContent } from '@/components/composite/MediaViewer/PostPreview/PreviewTwitter/QuotedTweetContent';
import { Status } from '@/components/composite/MediaViewer/PostPreview/PreviewTwitter/Status';
import { QuoteBadge } from '@/components/composite/Post/Twitter/QuoteBadge';
import { ReplyBadge } from '@/components/composite/Post/Twitter/ReplyBadge';
import { RetweetBadge } from '@/components/composite/Post/Twitter/RetweetBadge';

type PreviewTwitterProps = {
    post?: XMediaPartialType;
    isLoading?: boolean;
    className?: string;
};

export const PreviewTwitter = ({ post, isLoading, ...restProps }: PreviewTwitterProps) => {
    const { formatTextWithComponent } = useTransformStringOnOffset();

    if (isLoading) {
        return (
            <span className="square block animate-pulse rounded-md bg-icoGray-300 dark:bg-icoDarkMode-deepBlack" />
        );
    }

    const getFormattedCaption = (caption?: string, tags?: XTagType[]) => {
        if (!caption) return null;
        return formatTextWithComponent(caption, tags);
    };

    // Needed since links and polls are returned in the medias
    // Check both type and subtype for carousel, and fall back to all medias if image filter is empty
    const isCarousel =
        post?.type === TW_POST_TYPES.TYPE_CAROUSEL ||
        (post as { subtype?: string })?.subtype === TW_POST_TYPES.TYPE_CAROUSEL;

    const getCarouselMedia = () => {
        if (!isCarousel) return undefined;

        const imageOnlyMedia = post?.medias?.filter(
            (media) => media?.type === MEDIA_TYPES.TYPE_IMAGE
        );

        // Prefer images, fall back to all medias if no images found
        return imageOnlyMedia?.length ? imageOnlyMedia : post?.medias;
    };

    const carouselMedia = getCarouselMedia();

    return (
        <div
            className="group relative flex min-h-[400px] w-full flex-col overflow-hidden rounded-md bg-icoGray-100 p-16 dark:bg-icoDarkMode-deepBlack"
            aria-label="preview-twitter">
            {post?.type === TW_POST_TYPES.TYPE_RETWEET && (
                <>
                    <div className="mb-2 space-y-5">
                        <p className="text-xs">
                            <RetweetBadge />
                        </p>
                        <InitialTweetAuthor post={post} />
                    </div>
                    <Status
                        caption={getFormattedCaption(
                            post?.data?.related_tweet?.caption ?? post?.caption,
                            post?.data?.related_tweet?.tags ?? post?.tags
                        )}
                        tags={post?.data?.related_tweet?.tags ?? post?.tags}
                        className="mb-8 font-GMedium text-lg"
                    />
                    {/* Show media for retweets if available */}
                    {post?.medias?.[0]?.type === MEDIA_TYPES.TYPE_IMAGE &&
                        (post?.medias?.length === 1 ? (
                            // @ts-ignore
                            <Item
                                post={post}
                                postType={POST}
                                mediaId={0}
                                media={post?.medias[0]}
                                config={{ withAltText: false, imgFit: 'cover' }}
                                aria-label="retweet-media"
                                network={X}
                                {...restProps}
                            />
                        ) : (
                            <Carousel
                                config={{ slidesToScroll: 1 }}
                                hasDots
                                navPosition="external"
                                dotVariant="darken"
                                navOnHover={true}
                                aria-label="retweet-carousel">
                                {post?.medias
                                    ?.filter((m) => m?.type === MEDIA_TYPES.TYPE_IMAGE)
                                    ?.map((media, index) => {
                                        const mediaKey =
                                            media?.image ??
                                            media?.thumbnail ??
                                            media?.link ??
                                            `retweet-carousel-${index}`;
                                        return (
                                            <Carousel.Slide key={mediaKey} size="full">
                                                {/* @ts-ignore */}
                                                <Item
                                                    post={post}
                                                    postType={POST}
                                                    mediaId={index}
                                                    media={media}
                                                    config={{ withAltText: false, imgFit: 'cover' }}
                                                    network={X}
                                                    {...restProps}
                                                />
                                            </Carousel.Slide>
                                        );
                                    })}
                            </Carousel>
                        ))}
                    {post?.medias?.[0]?.type === MEDIA_TYPES.TYPE_VIDEO && (
                        // @ts-ignore
                        <Item
                            post={post}
                            postType={POST}
                            mediaId={0}
                            media={post?.medias[0]}
                            config={{ withAltText: false, imgFit: 'cover' }}
                            aria-label="retweet-video"
                            network={X}
                            {...restProps}
                        />
                    )}
                </>
            )}
            {post?.type === TW_POST_TYPES.TYPE_QUOTE && (
                <>
                    <p className="mb-5 text-xs">
                        <QuoteBadge />
                    </p>
                    <Status
                        caption={getFormattedCaption(post?.caption, post?.tags)}
                        tags={post?.tags}
                        className={'mb-2 font-GMedium text-lg'}
                    />
                    {/* Show related tweet info if available, otherwise show link to quoted tweet */}
                    <QuotedTweetContent post={post} getFormattedCaption={getFormattedCaption} />
                </>
            )}

            {post?.type === TW_POST_TYPES.TYPE_REPLY && (
                <>
                    <div className="mb-2">
                        <p className="mb-5 text-xs">
                            <ReplyBadge />
                        </p>
                        <InitialTweetAuthor post={post} />
                    </div>
                    <Status
                        caption={getFormattedCaption(post?.caption, post?.tags)}
                        tags={post?.tags}
                        className="mb-8 font-GMedium text-lg"
                    />
                </>
            )}

            {post?.type !== TW_POST_TYPES.TYPE_REPLY &&
                post?.type !== TW_POST_TYPES.TYPE_QUOTE &&
                post?.type !== TW_POST_TYPES.TYPE_RETWEET && (
                    <Status
                        aria-label="twitter-status"
                        caption={getFormattedCaption(post?.caption, post?.tags)}
                        tags={post?.tags}
                        className={classNames('font-GMedium text-xl', {
                            'mb-8': post?.type !== TW_POST_TYPES.TYPE_STATUS
                        })}
                    />
                )}

            {post?.type === TW_POST_TYPES.TYPE_POLL && post?.medias?.[0]?.poll?.options && (
                <div className="mt-5 flex flex-col space-y-5 text-base" aria-label="twitter-poll">
                    {post?.medias[0]?.poll?.options?.map((option) => (
                        <div
                            key={`poll-${option?.label}`}
                            className="flex items-center justify-between">
                            <span>{option?.label}</span>
                            {option?.percentage !== undefined && option?.percentage !== null && (
                                <span>{option?.percentage}%</span>
                            )}
                        </div>
                    ))}
                </div>
            )}

            {post?.type === TW_POST_TYPES.TYPE_LINK && post?.medias?.[0] && (
                <a
                    href={post?.medias[0]?.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex min-w-150 items-center justify-between overflow-hidden rounded border border-icoGray-300 bg-white p-5 dark:border-icoDarkMode-beton dark:bg-icoDarkMode-anthracite"
                    aria-label="twitter-link">
                    <div className="flex flex-col space-y-1">
                        <span className="text-xxs">
                            {(post?.medias[0] as { text?: string })?.text}
                        </span>
                        <p className="flex items-center text-icoGray-500 dark:text-icoDarkMode-wolf">
                            <Icon icon="link" className="text-xxs" />
                            <span className="ml-2 text-xxs">
                                {(post?.medias[0]?.link?.length ?? 0) > 30
                                    ? `${post?.medias[0]?.link?.substring(0, 30)}...`
                                    : post?.medias[0]?.link}
                            </span>
                        </p>
                    </div>
                    <Icon
                        icon="chevron-right"
                        className="text-xs text-icoGray-500 dark:text-icoDarkMode-wolf"
                    />
                </a>
            )}
            {(post?.type === TW_POST_TYPES.TYPE_PHOTO || post?.type === TW_POST_TYPES.TYPE_VIDEO) &&
                post?.medias?.[0] && (
                    // @ts-ignore
                    <Item
                        post={post}
                        postType={POST}
                        mediaId={0}
                        media={post?.medias[0]}
                        config={{ withAltText: false, imgFit: 'cover' }}
                        aria-label="twitter-media"
                        network={X}
                        {...restProps}
                    />
                )}

            {isCarousel && carouselMedia && carouselMedia.length > 0 && (
                <Carousel
                    config={{ slidesToScroll: 1 }}
                    hasDots
                    navPosition="external"
                    dotVariant="darken"
                    navOnHover={true}
                    aria-label="twitter-carousel">
                    {carouselMedia?.map((media, index) => {
                        const mediaKey =
                            media?.image ?? media?.thumbnail ?? media?.link ?? `carousel-${index}`;
                        return (
                            <Carousel.Slide key={mediaKey} size="full">
                                {/* @ts-ignore */}
                                <Item
                                    post={post}
                                    postType={POST}
                                    mediaId={index}
                                    media={media}
                                    config={{ withAltText: false, imgFit: 'cover' }}
                                    network={X}
                                    {...restProps}
                                />
                            </Carousel.Slide>
                        );
                    })}
                </Carousel>
            )}
        </div>
    );
};
